import React, { useState, useEffect } from 'react'
import jsPDF from 'jspdf'
import html2canvas from 'html2canvas'
import { notifications } from '@mantine/notifications'
import { useAuthStore } from '../../store/useAuthStore'
import { apiRequest } from '../../utils/api'
import { buildMonthlyReportPages } from '../../utils/reportPdfBuilder'
import { Icon } from '../../assets/icons'

const MON_FULL  = ['','January','February','March','April','May','June','July','August','September','October','November','December']
const MON_SHORT = ['','JAN','FEB','MAR','APR','MAY','JUN','JUL','AUG','SEP','OCT','NOV','DEC']
const PAGE_SIZE = 5

const MyReportsBox = () => {
  const user    = useAuthStore((s) => s.user)
  const isParent = user?.STATUS === 'P'

  const [reports, setReports]             = useState([])
  const [isLoading, setIsLoading]         = useState(false)
  const [downloadingKey, setDownloadingKey] = useState(null)
  const [page, setPage]                   = useState(1)

  useEffect(() => {
    if (!user?.ID) return
    setIsLoading(true)
    apiRequest(`/report/my-reports?NUMERIC_ID=${user.ID}&STATUS=${user.STATUS}`, 'GET')
      .then((res) => { if (res?.success) setReports(res.reports || []) })
      .catch(() => notifications.show({ title: 'Error', message: 'Failed to load reports', color: 'red' }))
      .finally(() => setIsLoading(false))
  }, [user])

  const handleDownload = async (report) => {
    const key = `${report.USER_ID}-${report.year}-${report.month}`
    setDownloadingKey(key)
    try {
      const res = await apiRequest('/report/saved', 'GET', {
        USER_ID: report.USER_ID, year: report.year, month: report.month,
      })
      if (!res?.found) {
        notifications.show({ title: 'Not found', message: 'Report data not available', color: 'yellow' })
        return
      }
      const pages    = buildMonthlyReportPages(res.data)
      const pdf      = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' })
      const pdfWidth = pdf.internal.pageSize.getWidth()
      for (let i = 0; i < pages.length; i++) {
        const container = document.createElement('div')
        container.style.cssText = 'position:fixed;left:-9999px;top:0;width:870px;background:#F8FAFC;'
        container.innerHTML = pages[i]
        document.body.appendChild(container)
        await new Promise((r) => setTimeout(r, 350))
        const canvas = await html2canvas(container, { scale: 2, useCORS: true, backgroundColor: '#F8FAFC' })
        document.body.removeChild(container)
        const imgH = (canvas.height * pdfWidth) / canvas.width
        if (i > 0) pdf.addPage()
        pdf.addImage(canvas.toDataURL('image/png'), 'PNG', 0, 0, pdfWidth, imgH)
      }
      const name = report.NAME || user?.NAME || 'report'
      pdf.save(`report_${name}_${report.year}-${String(report.month).padStart(2,'0')}.pdf`)
      notifications.show({ title: 'Downloaded', message: 'Report downloaded', color: 'green' })
    } catch (e) {
      notifications.show({ title: 'Error', message: 'Failed to generate PDF', color: 'red' })
    } finally {
      setDownloadingKey(null)
    }
  }

  const grouped = isParent
    ? reports.reduce((acc, r) => {
        if (!acc[r.USER_ID]) acc[r.USER_ID] = { name: r.NAME, items: [] }
        acc[r.USER_ID].items.push(r)
        return acc
      }, {})
    : null

  const totalPages = Math.ceil(reports.length / PAGE_SIZE)
  const paged = isParent ? null : reports.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE)

  return (
    <div style={{ padding: '32px 36px', maxWidth: 720 }}>

      {/* 헤더 */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 28 }}>
        <div style={{
          width: 48, height: 48, borderRadius: 12, background: '#EEF2FF',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <Icon name="overview" size={22} color="#6366F1" />
        </div>
        <div>
          <div style={{ fontSize: 22, fontWeight: 800, color: '#1a2f4c', letterSpacing: '-0.02em' }}>My Reports</div>
          <div style={{ fontSize: 12, color: '#9CA3AF', marginTop: 2 }}>
            {isParent ? "Your child's monthly learning reports" : 'Your monthly learning reports'}
          </div>
        </div>
      </div>

      {isLoading ? (
        <div style={{ color: '#9CA3AF', fontSize: 13, padding: '60px 0', textAlign: 'center' }}>Loading...</div>
      ) : reports.length === 0 ? (
        <div style={{
          padding: '60px 0', textAlign: 'center', color: '#9CA3AF', fontSize: 13,
          border: '1px dashed #E5E7EB', borderRadius: 16,
        }}>
          No reports available yet.
        </div>
      ) : isParent ? (
        Object.entries(grouped).map(([userId, { name, items }]) => (
          <div key={userId} style={{ marginBottom: 32 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: '#9CA3AF', letterSpacing: '0.08em', marginBottom: 10, textTransform: 'uppercase' }}>
              {name}
            </div>
            {items.map((r, idx) => (
              <ReportCard key={`${r.USER_ID}-${r.year}-${r.month}`} report={r} isLatest={idx === 0} downloadingKey={downloadingKey} onDownload={handleDownload} />
            ))}
          </div>
        ))
      ) : (
        <>
          {paged.map((r, idx) => (
            <ReportCard key={`${r.USER_ID}-${r.year}-${r.month}`} report={r} isLatest={page === 1 && idx === 0} downloadingKey={downloadingKey} onDownload={handleDownload} />
          ))}

          {/* 페이지네이션 */}
          {totalPages > 1 && (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 6, marginTop: 24 }}>
              <button onClick={() => setPage(p => Math.max(1, p-1))} disabled={page === 1}
                style={{ width: 32, height: 32, borderRadius: 8, border: '1px solid #E5E7EB', background: '#fff', cursor: page === 1 ? 'not-allowed' : 'pointer', color: '#374151', fontSize: 14 }}>
                ‹
              </button>
              {Array.from({ length: totalPages }, (_, i) => i+1).map(p => (
                <button key={p} onClick={() => setPage(p)}
                  style={{
                    width: 32, height: 32, borderRadius: 8, fontSize: 13, fontWeight: 700,
                    border: p === page ? 'none' : '1px solid #E5E7EB',
                    background: p === page ? '#1a2f4c' : '#fff',
                    color: p === page ? '#fff' : '#374151',
                    cursor: 'pointer',
                  }}>
                  {p}
                </button>
              ))}
              <button onClick={() => setPage(p => Math.min(totalPages, p+1))} disabled={page === totalPages}
                style={{ width: 32, height: 32, borderRadius: 8, border: '1px solid #E5E7EB', background: '#fff', cursor: page === totalPages ? 'not-allowed' : 'pointer', color: '#374151', fontSize: 14 }}>
                ›
              </button>
            </div>
          )}
        </>
      )}
    </div>
  )
}

const KpiChip = ({ iconName, value, label, color }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
    <div style={{
      width: 36, height: 36, borderRadius: 10, background: color + '18',
      display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
    }}>
      <Icon name={iconName} size={18} color={color} />
    </div>
    <div>
      <div style={{ fontSize: 15, fontWeight: 800, color: '#1a2f4c', lineHeight: 1 }}>{value}</div>
      <div style={{ fontSize: 10, color: '#9CA3AF', marginTop: 2, whiteSpace: 'nowrap' }}>{label}</div>
    </div>
  </div>
)

const ReportCard = ({ report: r, isLatest, downloadingKey, onDownload }) => {
  const key  = `${r.USER_ID}-${r.year}-${r.month}`
  const isDl = downloadingKey === key

  return (
    <div style={{
      background: '#fff', border: '1px solid #E8ECF4', borderRadius: 16,
      padding: '20px 24px', marginBottom: 12,
      boxShadow: isLatest ? '0 2px 12px rgba(26,47,76,0.07)' : 'none',
    }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 16 }}>

        {/* 날짜 블록 */}
        <div style={{
          width: 60, minWidth: 60, height: 60, borderRadius: 12,
          background: '#EEF2FF', display: 'flex', flexDirection: 'column',
          alignItems: 'center', justifyContent: 'center',
        }}>
          <div style={{ fontSize: 9, fontWeight: 700, color: '#6B7280', lineHeight: 1 }}>{r.year}</div>
          <div style={{ fontSize: 20, fontWeight: 900, color: '#1a2f4c', lineHeight: 1.1 }}>
            {String(r.month).padStart(2,'0')}
          </div>
          <div style={{ fontSize: 8, fontWeight: 700, color: '#6B7280', lineHeight: 1, letterSpacing: '0.05em' }}>
            {MON_SHORT[r.month]}
          </div>
        </div>

        {/* 본문 */}
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 2 }}>
            <div style={{ fontSize: 16, fontWeight: 800, color: '#1a2f4c' }}>
              {MON_FULL[r.month]} {r.year}
            </div>
            {isLatest && (
              <span style={{
                padding: '2px 9px', borderRadius: 20, fontSize: 10, fontWeight: 700,
                background: '#EEF2FF', color: '#6366F1',
              }}>Latest</span>
            )}
          </div>
          <div style={{ fontSize: 11, color: '#9CA3AF', marginBottom: 14 }}>Monthly Learning Report</div>

          {/* KPI 칩 */}
          {(r.masteryAccuracy || r.paceTopPct || r.activeDays) && (
            <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap', marginBottom: 14 }}>
              {r.masteryAccuracy != null && (
                <KpiChip iconName="writing" value={`${r.masteryAccuracy}%`} label="Mastery Accuracy" color="#6366F1" />
              )}
              {r.paceTopPct != null && (
                <KpiChip iconName="data" value={`Top ${r.paceTopPct}%`} label="Learning Pace" color="#10B981" />
              )}
              {r.activeDays != null && (
                <KpiChip iconName="class" value={r.activeDays} label="Active Days" color="#F59E0B" />
              )}
            </div>
          )}

          <div style={{ fontSize: 11, color: '#C4C9D4' }}>
            Generated {new Date(r.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
          </div>
        </div>

        {/* 다운로드 버튼 */}
        <button
          onClick={() => onDownload(r)}
          disabled={isDl}
          style={{
            display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0, alignSelf: 'center',
            padding: '10px 18px', borderRadius: 10, fontSize: 12, fontWeight: 700,
            background: isDl ? '#F3F4F6' : isLatest ? '#1a2f4c' : '#fff',
            color: isDl ? '#9CA3AF' : isLatest ? '#fff' : '#1a2f4c',
            border: isLatest ? 'none' : '1.5px solid #1a2f4c',
            cursor: isDl ? 'not-allowed' : 'pointer',
            transition: 'opacity 0.15s',
          }}
        >
          {isDl ? (
            'Preparing...'
          ) : (
            <>
              <span style={{ fontSize: 14 }}>📄</span>
              View Report <span style={{ fontSize: 13 }}>›</span>
            </>
          )}
        </button>
      </div>
    </div>
  )
}

export default MyReportsBox
